// Staff day view: today's queue or any day (calendar), with an owner barber filter.
import { useCallback, useEffect, useState } from 'react';
import type { StaffBooking } from '../api/types.ts';
import { useShop } from '../app/boot.tsx';
import { StaffTabs } from '../app/nav.tsx';
import { message } from '../lib/errors.ts';
import { shiftDate, thaiDate, timeOf, todayLocal } from '../lib/time.ts';
import { Banner, Button, Chip, Empty, Loading, Page } from '../ui/index.tsx';
import { BookingSheet } from './BookingSheet.tsx';
import { STATUS_LABEL } from './status.ts';

export function Day({ mode }: { mode: 'today' | 'calendar' }) {
  const { api, shop, isOwner } = useShop();
  const [date, setDate] = useState(todayLocal());
  const [barberId, setBarberId] = useState<string | undefined>(undefined);
  const [rows, setRows] = useState<StaffBooking[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<StaffBooking | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await api.staff.bookings(date, barberId));
    } catch (e) {
      setError(message(e));
      setRows([]);
    }
  }, [api, date, barberId]);

  useEffect(() => { setRows(null); void load(); }, [load]);

  const active = (rows ?? []).filter((b) => b.status !== 'cancelled_customer' && b.status !== 'cancelled_shop');
  const cancelled = (rows ?? []).filter((b) => b.status === 'cancelled_customer' || b.status === 'cancelled_shop');
  const isToday = date === todayLocal();

  return (
    <>
      <Page title={mode === 'today' ? 'คิววันนี้' : 'ปฏิทิน'} actions={<Button variant="ghost" className="btn-sm" onClick={() => void load()} aria-label="รีเฟรช">↻</Button>}>
        {mode === 'calendar' && (
          <div className="row-between" style={{ marginBottom: 12 }}>
            <Button variant="secondary" className="btn-sm" onClick={() => setDate(shiftDate(date, -1))} aria-label="วันก่อน">‹</Button>
            <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="เลือกวัน"
              style={{ minHeight: 40, border: '1.5px solid var(--line-strong)', borderRadius: 10, padding: '0 10px', font: 'inherit' }} />
            <Button variant="secondary" className="btn-sm" onClick={() => setDate(shiftDate(date, 1))} aria-label="วันถัดไป">›</Button>
          </div>
        )}
        <p className="muted small" style={{ marginBottom: 8 }}>{thaiDate(date)}{isToday ? ' (วันนี้)' : ''} · {active.length} คิว</p>
        {isOwner && shop.barbers.length > 1 && (
          <div className="filter-chips" role="group" aria-label="กรองตามช่าง">
            <button type="button" className={barberId === undefined ? 'active' : ''} onClick={() => setBarberId(undefined)}>ทั้งหมด</button>
            {shop.barbers.map((b) => (
              <button key={b.id} type="button" className={barberId === b.id ? 'active' : ''} onClick={() => setBarberId(b.id)}>{b.name}</button>
            ))}
          </div>
        )}
        {error && <Banner kind="error" action={{ label: 'ลองใหม่', onClick: () => void load() }}>{error}</Banner>}
        {rows === null ? <Loading /> : active.length === 0 && cancelled.length === 0 ? (
          <Empty title="ไม่มีคิวในวันนี้" />
        ) : (
          <div className="list">
            {active.map((b) => <Row key={b.id} b={b} showBarber={isOwner && !barberId} onOpen={() => setOpen(b)} />)}
            {cancelled.length > 0 && <p className="section-title">ยกเลิกแล้ว</p>}
            {cancelled.map((b) => <Row key={b.id} b={b} showBarber={isOwner && !barberId} onOpen={() => setOpen(b)} />)}
          </div>
        )}
      </Page>
      <BookingSheet booking={open} onClose={() => setOpen(null)} onChanged={() => void load()} />
      <StaffTabs owner={isOwner} />
    </>
  );
}

function Row({ b, showBarber, onOpen }: { b: StaffBooking; showBarber: boolean; onOpen: () => void }) {
  const s = STATUS_LABEL[b.status];
  return (
    <button type="button" className="card card-button" onClick={onOpen}>
      <div className="booking-row">
        <span className="slot-time">{timeOf(b.startAt)}</span>
        <div className="grow">
          <div className="card-title">{b.customerName}</div>
          <div className="muted small">{b.serviceName}{showBarber ? ` · ช่าง${b.barberName}` : ''}</div>
          <div className="status-line">
            <Chip tone={s.tone}>{s.label}</Chip>
            {b.noShowCount > 0 && <Chip tone="accent">เคยไม่มา {b.noShowCount} ครั้ง</Chip>}
            {b.sharedPhone && <Chip>เบอร์ซ้ำ</Chip>}
            {!b.hasLine && <Chip tone="muted">ไม่มี LINE</Chip>}
          </div>
        </div>
      </div>
    </button>
  );
}
